/**
 * Shared pieces of the ops Grading page (ADR-133 Amendment 2 §1; COMMUNITY build 3 Stage B) — the loading state, the
 * action icon (coloured by meaning, its hover tooltip opening upward), the (?) value help, and the zone fan-out every
 * list on the page reads through. Markup and class names are the ops mock's (version 13), so grading.css — extracted
 * from it — styles them as signed off.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '../../../components/Tooltip';
import { useToast, toastFromError } from '../../../components/ToastProvider';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone, type ZoneResult } from '../../../lib/zones';
import { mergeZoneRows, type Zoned } from '../../../lib/zone-board';
import type { GradingCan } from '../../../lib/grading-api';
import { formatDate } from '../../../lib/time-format';

/** What the caller may do — learned from the service (GET /admin/grading/me), never inferred from a role name. */
export interface PageCtx { can: GradingCan }

/** The layered loading indicator — a skeleton AND a status line, in tandem (the standing directive). */
export function Loading({ text }: { text: string }) {
  return (
    <div role="status" aria-live="polite" data-testid="grading-loading">
      <div className="portal-orphans-skeleton" aria-hidden="true"><span /><span /><span /></div>
      <p className="panel-help" style={{ margin: '.5rem 0 0' }}>{text}</p>
    </div>
  );
}

const ICONS: Record<string, ReactNode> = {
  view: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  edit: <><path d="M4 20h4L19 9l-4-4L4 16z" /><path d="M13.5 6.5l4 4" /></>,
  keep: <path d="M5 12l5 5 9-11" />,
  quarantine: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>,
  remove: <><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6l1 14h10l1-14" /></>,
  queue: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  // s9-outcome-report — the Testimonials tab's Unpublish (off the board, kept)
  unpublish: <><path d="M3 3l18 18" /><path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c7 0 11 7 11 7a18.5 18.5 0 0 1-3.2 4.1M6.6 6.6C3.4 8.4 1 12 1 12s4 7 11 7a10.7 10.7 0 0 0 5.4-1.4" /><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" /></>,
};

/**
 * An action button in an Actions column: the ops icon button, coloured by meaning through its `data-act` (preview
 * neutral · edit blue · keep teal · quarantine / remove red — grading.css), with its own hover tooltip naming the action.
 */
export function ActionIcon({ act, icon, label, tip, onClick, href }: {
  act: string; icon: keyof typeof ICONS; label: string; tip: string; onClick?: () => void; href?: string;
}) {
  const svg = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[icon]}</svg>
  );
  return (
    <Tooltip content={tip} aria={tip}>
      {href
        ? <Link to={href} className="portal-action-icon-btn" data-act={act} aria-label={label}>{svg}</Link>
        : <button type="button" className="portal-action-icon-btn" data-act={act} aria-label={label} onClick={onClick}>{svg}</button>}
    </Tooltip>
  );
}

/** A dismissable dialog in the console's pattern: the backdrop, the modal, the circle close top right (.portal-modal-close). */
export function Dialog({ title, onClose, children, actions, wide }: { title: string; onClose: () => void; children: ReactNode; actions?: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="portal-modal-backdrop" role="presentation">
      <div className={`portal-modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} style={wide ? { maxWidth: '60rem' } : undefined}>
        <button type="button" className="portal-modal-close" aria-label="Close dialog" onClick={onClose}>×</button>
        <h2>{title}</h2>
        <div className="portal-action-modal-body">{children}</div>
        <div className="portal-modal-actions">{actions ?? <button type="button" className="portal-link-button-secondary" onClick={onClose}>Close</button>}</div>
      </div>
    </div>
  );
}

/** The (?) beside a value: what the setting does, opening on hover or focus (the mock's .qh). */
export function ValueHelp({ label, children }: { label: string; children: ReactNode }) {
  return (
    <button type="button" className="qh" aria-label={`What this means — ${label}`}>
      <span className="qt" role="tooltip">{children}</span>?
    </button>
  );
}

/**
 * A list read fanned out across every configured zone, each row tagged with the zone it came from (ADR-122 Pattern 1).
 * One unreachable zone is a labelled partial read; every zone failing is a failed read, toasted, with Retry.
 */
export function useZoneList<T extends object>(read: (z: PortalZone) => Promise<T[]>, what: string, deps: unknown[]) {
  const toast = useToast();
  const [rows, setRows] = useState<Zoned<T>[] | null>(null);
  const [results, setResults] = useState<ZoneResult<T>[] | null>(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await fanOutZones(read);
      throwIfAllZonesFailed(res);
      setResults(res);
      setRows(mergeZoneRows(res));
      const missing = failedZoneLabels(res);
      if (missing.length) toast.push({ variant: 'info', title: `Some zones did not answer`, detail: `${what}: ${missing.join(', ')} did not respond — the rest are shown.` });
    } catch (err) {
      setFailed(true);
      setRows([]);
      const { title, detail } = toastFromError(err, `Could not load ${what}`);
      toast.push({ variant: 'error', title, detail });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { void load(); }, [load]);
  return { rows, results, failed, reload: load };
}

/** A day as the console writes it ("29 Oct 2026", the viewer's zone) — the one formatter, lib/time-format.tsx. */
export const fmtDate = (iso: string | null | undefined): string => formatDate(iso, 'medium');
